import { useState } from "react";
import { useRouter } from "expo-router";
import { Pressable, StyleSheet, View } from "react-native";

import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppModal,
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  EmptyState,
  ListRow,
  MetricCard,
  ProtectedScreen,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import { usePlan } from "~/hooks/usePlan";
import { useListActions, useLists } from "~/state/lists";
import { useTasks } from "~/state/tasks";
import { LIST_COLORS } from "~/utils/cadence";
import {
  radii,
  spacing,
  useTheme,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

export default function ListsScreen() {
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStyles);
  const router = useRouter();
  const plan = usePlan();
  const lists = useLists();
  const tasks = useTasks();
  const actions = useListActions();
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>(LIST_COLORS[0]);
  const [error, setError] = useState<string | null>(null);

  function create() {
    if (!name.trim()) return;
    try {
      const id = actions.create({ name, color });
      setName("");
      setColor(LIST_COLORS[0]);
      setError(null);
      setCreateOpen(false);
      router.push(`/lists/${id}` as never);
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "Unable to create this list.",
      );
    }
  }

  return (
    <ProtectedScreen
      title="Lists"
      subtitle="Group the weekly chores, the monthly admin, and the one-offs."
    >
      <View style={stylesheet.metrics}>
        <View style={stylesheet.metric}>
          <MetricCard
            label="Lists"
            value={
              plan.lists.limit === null
                ? String(lists.length)
                : `${lists.length} / ${plan.lists.limit}`
            }
          />
        </View>
        <View style={stylesheet.metric}>
          <MetricCard
            label="Tasks"
            value={
              plan.tasks.limit === null
                ? String(tasks.length)
                : `${tasks.length} / ${plan.tasks.limit}`
            }
          />
        </View>
      </View>

      <Card gap="md">
        <SectionHeader
          title="Your lists"
          description="Everything is stored on this device first, then synced when you have a connection."
        />
        {lists.length === 0 ? (
          <EmptyState
            title="No lists yet"
            description="Create a list and add the tasks you do not want to forget."
          />
        ) : (
          <Stack gap="sm">
            {lists.map((list) => {
              const count = tasks.filter(
                (task) => task.list_id === list.id,
              ).length;
              return (
                <ListRow
                  key={list.id}
                  title={list.name}
                  onPress={() => router.push(`/lists/${list.id}` as never)}
                  meta={
                    <>
                      <Badge tone="neutral">
                        {count} {count === 1 ? "task" : "tasks"}
                      </Badge>
                      {list.archived ? (
                        <Badge tone="warning">Archived</Badge>
                      ) : null}
                    </>
                  }
                  trailing={
                    <View
                      style={[
                        stylesheet.swatch,
                        { backgroundColor: list.color ?? colors.primary },
                      ]}
                    />
                  }
                />
              );
            })}
          </Stack>
        )}

        {plan.lists.allowed ? (
          <Button
            align="start"
            iconName="plus"
            onPress={() => setCreateOpen(true)}
          >
            <ButtonText>New list</ButtonText>
          </Button>
        ) : (
          <AppText size="xs">{plan.lists.message}</AppText>
        )}
      </Card>

      {!plan.lists.allowed ? (
        <UpgradePrompt title="Want more than one list?" />
      ) : null}

      <AppModal
        title="New list"
        visible={createOpen}
        onClose={() => setCreateOpen(false)}
      >
        <Stack gap="md">
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Name
            </AppText>
            <TextField
              accessibilityLabel="List name"
              value={name}
              placeholder="Household, Work, Admin…"
              onChangeText={setName}
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Colour
            </AppText>
            <View style={stylesheet.swatches}>
              {LIST_COLORS.map((option) => (
                <Pressable
                  key={option}
                  accessibilityRole="button"
                  accessibilityLabel={`Colour ${option}`}
                  onPress={() => setColor(option)}
                  style={[
                    stylesheet.swatchButton,
                    { backgroundColor: option },
                    color === option ? stylesheet.swatchSelected : null,
                  ]}
                />
              ))}
            </View>
          </View>
          {error ? (
            <AppText size="xs" tone="danger">
              {error}
            </AppText>
          ) : null}
          <Button iconName="plus" disabled={!name.trim()} onPress={create}>
            <ButtonText>Create list</ButtonText>
          </Button>
        </Stack>
      </AppModal>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    metrics: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
    metric: { minWidth: 180, flex: 1 },
    field: { gap: spacing.xs },
    swatch: { width: 16, height: 16, borderRadius: 8 },
    swatches: { flexDirection: "row", gap: spacing.s },
    swatchButton: {
      width: 28,
      height: 28,
      borderRadius: 14,
      borderWidth: 2,
      borderColor: "transparent",
    },
    swatchSelected: { borderColor: colors.heading },
  });
